/**
 * The ring registry: everything the scene needs to turn a bundle plus a view
 * layout into a tree of rings, and nothing that touches the DOM.
 *
 * `shared/rings.ts` does the geometry — where each circle's ring lands, which
 * ones are bands on a parent, which rings an event appears on. This module is
 * the layer above it that knows about *calendars*: it turns tags and a
 * `ViewLayout` into `RingNode`s, reconstructs the band weights a merged ring
 * hands out, builds the levels of the style cascade out of theme, circle,
 * source and event, and owns the entry-id scheme that lets one event have a
 * mark on several rings at once.
 *
 * The id scheme is the one part worth stating plainly, because selection and
 * every existing test depend on it: **a fragment on the root ring keeps the id
 * it always had** (`ringMark:evt1`, `float:evt1`), and a fragment on any other
 * ring appends `@<ringId>` (`ringMark:evt1@tag_work`). A calendar with no
 * circles therefore renders exactly the ids it rendered before circles existed.
 */
import {
  ROOT_RING_ID,
  bandAnnulus,
  effectiveProminence,
  layoutRings,
  placementsByEvent,
  ringBand,
  type CalEvent,
  type ResolvedStyle,
  type RingBand,
  type RingMode,
  type RingNode,
  type RingPlacement,
  type Source,
  type StyleTokens,
  type Tag,
  type ViewLayout,
  type ViewNode,
} from '@circle/shared';

export { ROOT_RING_ID } from '@circle/shared';

/** What a calendar looks like before anyone has saved a view: one merged ring. */
export const DEFAULT_VIEW_LAYOUT: ViewLayout = Object.freeze({
  nodes: { [ROOT_RING_ID]: { mode: 'merged', visible: true } },
}) as ViewLayout;

/** What a node does when the layout says nothing about it. */
export const DEFAULT_VIEW_NODE: ViewNode = Object.freeze({ mode: 'merged', visible: true });

/* --------------------------------------------------------------- entry ids --- */

/** `''` for the root ring, `'@tag_work'` for any other. */
export function ringSuffix(ringId: string): string {
  return ringId === ROOT_RING_ID ? '' : `@${ringId}`;
}

/** `ringMark:evt1` on the root, `ringMark:evt1@tag_work` elsewhere. */
export function markIdFor(eventId: string, ringId: string): string {
  return `ringMark:${eventId}${ringSuffix(ringId)}`;
}

export function floatIdFor(eventId: string, ringId: string): string {
  return `float:${eventId}${ringSuffix(ringId)}`;
}

/** Suffix any scene id — a cluster's, a ring fragment's — with its ring. */
export function onRing(id: string, ringId: string): string {
  return `${id}${ringSuffix(ringId)}`;
}

/** The ring an entry id names, `'root'` when it carries no suffix. */
export function ringOfEntryId(entryId: string): string {
  const at = entryId.lastIndexOf('@');
  return at === -1 ? ROOT_RING_ID : entryId.slice(at + 1);
}

/* ------------------------------------------------------------------ nodes --- */

function clampProminence(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : 1;
}

/**
 * Tags plus the implicit root, as the layout engine wants them. Every node
 * takes its mode, placement, visibility and manual override from the view
 * layout, and its prominence and sort order from the tag itself — the layout
 * is *how you are looking* at the tree, the tags are what the tree is.
 */
export function ringNodes(tags: readonly Tag[], layout: ViewLayout = DEFAULT_VIEW_LAYOUT): RingNode[] {
  const nodesOf = layout?.nodes ?? {};
  const nodeFor = (id: string): ViewNode => nodesOf[id] ?? DEFAULT_VIEW_NODE;

  const root = nodeFor(ROOT_RING_ID);
  const out: RingNode[] = [
    {
      id: ROOT_RING_ID,
      parentId: null,
      prominence: 1,
      sort: 0,
      mode: root.mode ?? 'merged',
      visible: root.visible !== false,
    },
  ];

  for (const tag of tags ?? []) {
    if (!tag || typeof tag.id !== 'string') continue;
    const node = nodeFor(tag.id);
    const entry: RingNode = {
      id: tag.id,
      parentId: tag.parentId ?? ROOT_RING_ID,
      prominence: clampProminence(tag.prominence),
      sort: typeof tag.sort === 'number' && Number.isFinite(tag.sort) ? tag.sort : 0,
      mode: node.mode ?? 'merged',
      visible: node.visible !== false,
    };
    // A child's own say on how it sits on its parent; absent, the parent's
    // mode decides, which `layoutRings` works out for itself.
    if (node.placement) entry.placement = node.placement;
    // Verbatim, straight through to `layoutRings`, which places an overridden
    // ring exactly where it was dragged and leaves it out of the packing.
    if (node.override) entry.override = node.override;
    out.push(entry);
  }
  return out;
}

export function indexBy<T extends { id: string }>(items: readonly T[]): Map<string, T> {
  const out = new Map<string, T>();
  for (const item of items ?? []) if (item && typeof item.id === 'string') out.set(item.id, item);
  return out;
}

/** Root … leaf, the way the cascade wants it. Cycle-safe, and empty for the root ring. */
export function tagPathOf(tagId: string, tagsById: ReadonlyMap<string, Tag>): Tag[] {
  const out: Tag[] = [];
  const seen = new Set<string>();
  let cursor: string | null = tagId;
  while (cursor && cursor !== ROOT_RING_ID && !seen.has(cursor)) {
    seen.add(cursor);
    const tag: Tag | undefined = tagsById.get(cursor);
    if (!tag) break;
    out.push(tag);
    cursor = tag.parentId;
  }
  return out.reverse();
}

/* ------------------------------------------------------------------ bands --- */

/**
 * The prominence weights a merged ring shares out among its bands, in band
 * order. `ringBand` needs them all at once, and a placement only knows its own
 * index — so the siblings are read back off the placement list.
 */
export function bandWeights(
  placements: readonly RingPlacement[],
  parentId: string | null,
  nodesById: ReadonlyMap<string, RingNode>,
): number[] {
  const siblings = placements
    .filter((p) => p.parentId === parentId && p.bandIndex !== undefined)
    .sort((a, b) => (a.bandIndex ?? 0) - (b.bandIndex ?? 0));
  return siblings.map((p) => clampProminence(nodesById.get(p.id)?.prominence));
}

/**
 * Where a band's events sit: the middle of its annulus.
 *
 * The annulus is a tree ring just inside the line — a few percent of the radius
 * deep — so the middle of it is on the ring for every practical purpose, and
 * the single-band case falls out rather than needing an exception: a lone band
 * at the top level *is* the ring line (a zero-depth annulus at `r`), so its
 * middle is `r` and a merged circle with one child still looks like one with
 * none. `ringRadius` is the answer for a ring with no size at all.
 */
export function bandEventRadius(band: RingBand, bandCount: number, ringRadius: number): number {
  const mid = (band.rInner + band.rOuter) / 2;
  return mid > 0 || bandCount > 1 ? mid : ringRadius;
}

/**
 * Bands collapsed into one: every band placement keeps its parent's transform
 * but stops claiming a slice of it. This is the D3-a fallback the scene reaches
 * for when the camera is too far out for tree rings to read (CIRCLES.md §2).
 */
export function collapseBands(placements: readonly RingPlacement[]): RingPlacement[] {
  return placements.map((p) => {
    if (p.bandIndex === undefined) return p;
    // The nesting goes with the slices: a collapsed band is the ring line, at
    // every depth, so there is no annulus left for a nested one to sit in.
    const { bandPath: _nesting, ...rest } = p;
    return { ...rest, bandIndex: 0, bandCount: 1 };
  });
}

/* ------------------------------------------------------------ events → rings --- */

/**
 * Which rings one event is drawn on, and — when it is drawn once — which
 * circles it is in but not drawn on.
 *
 * The mode handed to `placementsByEvent` is not any one node's setting: it is
 * whether *this event's* rings are all bands. An event whose circles are all
 * tree rings on one merged ring can only be drawn once, in the loudest of them;
 * an event with a circle that has a ring of its own is drawn on each of them.
 */
export function ringsForEvent(
  event: CalEvent,
  placements: readonly RingPlacement[],
  placementById: ReadonlyMap<string, RingPlacement>,
  nodesById: ReadonlyMap<string, RingNode>,
): { rings: string[]; merged: boolean; others: string[] } {
  const tagIds = (event.tagIds ?? []).filter((id) => placementById.has(id));
  const merged =
    tagIds.length > 0 && tagIds.every((id) => placementById.get(id)?.bandIndex !== undefined);
  const mode: RingMode = merged ? 'merged' : 'split';
  const rings = placementsByEvent(event.tagIds ?? [], placements, nodesById, mode);
  const shown = new Set(rings);
  return { rings, merged, others: tagIds.filter((id) => !shown.has(id)) };
}

/* ------------------------------------------------------------------ style --- */

/**
 * A circle as a level of the cascade. `colour` and `prominence` are columns on
 * the tag rather than tokens in its `style_json`, so they are folded in here:
 * the style's own colour wins over the column, and its own prominence
 * *multiplies* the column, because prominence is a weight at every level.
 */
export function tagStyleLevel(tag: Tag): StyleTokens {
  const level: StyleTokens = { ...(tag.style ?? {}) };
  if (level.colour === undefined && tag.colour !== undefined) level.colour = tag.colour;
  const own = clampProminence(tag.prominence);
  const fromStyle = clampProminence(tag.style?.prominence);
  const product = own * fromStyle;
  if (product !== 1) level.prominence = product;
  else delete level.prominence;
  return level;
}

/** The event's own level: its tokens, with `events.prominence` folded into the weight. */
export function eventStyleLevel(event: CalEvent): StyleTokens {
  const level: StyleTokens = { ...(event.style ?? {}) };
  const own = clampProminence(event.prominence);
  const fromStyle = clampProminence(event.style?.prominence);
  const product = own * fromStyle;
  if (product !== 1) level.prominence = product;
  else delete level.prominence;
  return level;
}

/**
 * The source's level: its colour column as a token. Whether that colour shows
 * is the cascade's decision — `resolveStyle` reads a source's colour as a
 * default that yields to any circle on the path (docs/CIRCLES.md §5) — so this
 * only says what the source has, never where it ranks.
 */
export function sourceStyleLevel(source: Source | undefined): StyleTokens {
  return source?.colour === undefined ? {} : { colour: source.colour };
}

/** `Π path prominence × event.prominence`, the number §4 calls effective. */
export function eventProminence(tagPath: readonly Tag[], event: CalEvent): number {
  return effectiveProminence(
    tagPath.map((tag) => clampProminence(tag.prominence) * clampProminence(tag.style?.prominence)),
    clampProminence(event.prominence) * clampProminence(event.style?.prominence),
  );
}

/* ------------------------------------------------------------------ camera --- */

/**
 * Radius of the smallest circle about the world origin that holds every ring.
 *
 * About the *origin* rather than about the rings' own bounding box, because the
 * whole scene spins around the origin: a box that fitted a split layout at rest
 * would clip it a quarter turn later. Empty layouts fall back to `fallback` so
 * a camera always has something to fit.
 */
export function boundingRadius(placements: readonly RingPlacement[], fallback: number): number {
  let out = 0;
  for (const p of placements ?? []) {
    if (p.bandIndex !== undefined && p.bandIndex > 0) continue;
    const reach = Math.hypot(p.cx, p.cy) + p.r;
    if (reach > out) out = reach;
  }
  return out > 0 ? out : fallback;
}

/** Placements, laid out at `R`, with bands collapsed when the camera says so. */
export function layoutFor(
  tags: readonly Tag[],
  layout: ViewLayout,
  R: number,
  bandsCollapsed = false,
): { nodes: RingNode[]; placements: RingPlacement[] } {
  const nodes = ringNodes(tags, layout);
  const placements = layoutRings(nodes, { R });
  return { nodes, placements: bandsCollapsed ? collapseBands(placements) : placements };
}

export type { ResolvedStyle, RingBand, RingNode, RingPlacement };
export { bandAnnulus, ringBand };
